import { gql, useMutation } from '@apollo/client';
import { useCallback } from 'react';

import type { RecordExperimentAllocation } from '@growtherapy/analytics/client';

import { Server } from '../../apollo/types';

const RECORD_EXPERIMENT_ALLOCATION_MUTATION = gql`
  mutation RecordExperimentAllocation($values: RecordExperimentAllocationInput!) {
    recordExperimentAllocation(values: $values) {
      allocationValue
      allocatedAt
    }
  }
`;

type RecordExperimentAllocationMutationData = {
  recordExperimentAllocation?: {
    allocationValue: string;
    allocatedAt: string;
  } | null;
};

/**
 * Persistence callback for `useDelayedExperiment`. Writes (or reads back) the
 * allocation anchor via the monolith GraphQL API so portal and telehealth share
 * the same cutover clock.
 */
export function useRecordExperimentAllocation(): RecordExperimentAllocation {
  const [recordAllocation] = useMutation<RecordExperimentAllocationMutationData>(
    RECORD_EXPERIMENT_ALLOCATION_MUTATION,
    {
      context: { server: Server.MONOLITH },
    },
  );

  return useCallback<RecordExperimentAllocation>(
    async ({ experimentKey, allocationValue }) => {
      const { data } = await recordAllocation({
        variables: { values: { experimentKey, allocationValue } },
      });

      const record = data?.recordExperimentAllocation;
      if (!record) {
        throw new Error(`Recording experiment allocation for "${experimentKey}" returned no allocation`);
      }

      return record;
    },
    [recordAllocation],
  );
}
